/**
 * Theme tokens for Ipsum City.
 *
 * Brand (logo + primary/accent/nav) is per council.
 * Chrome (surfaces, text, borders) and semantic states are shared.
 * Chart categories stay independent of brand so a red council does not
 * collapse every series into shades of red.
 *
 * Logos stay on REACT_APP_HEADER_LOGO / BRAND_LOGO / CREST.
 * Compact nav uses CREST. navMark is contain | crop-left | crop-right
 * (override with REACT_APP_NAV_MARK) so wide wordmarks keep a readable mark.
 * Env vars override brand (and optionally chrome) without a code change.
 */

const HEX6 = /^#?([0-9a-fA-F]{6})$/;

/**
 * Corporate dark theme — same for every council.
 * Brand logos stay as-is; interaction blue is product, not council.
 */
export const DARK_CHROME = {
    surfacePage: '#101C2A',
    surfaceCard: '#1B2B3E',
    surfaceRaised: '#26394F',
    surfaceHover: '#314A64',
    textPrimary: '#E5EDF6',
    textSecondary: '#A9B9CB',
    textTertiary: '#93A6BB',
    borderSubtle: '#354B62',
};

export const DARK_PRODUCT = {
    primary: '#08A0E8',
    primaryMid: '#0790D0',
    primaryDark: '#0678B0',
    primaryLight: '#3BB6F0',
    accent: '#FFC400',
    navBg: '#0F1824',
    navFg: '#E6EDF5',
};

export const DARK_STATUS = {
    created: '#F5B942',
    createdBg: '#3A2D18',
    createdBorder: '#6B5424',
    accepted: '#FF6B6B',
    acceptedBg: '#3B2027',
    acceptedBorder: '#6B3238',
    started: '#46B5FF',
    startedBg: '#19334A',
    startedBorder: '#2A5578',
    wip: '#35D0C1',
    wipBg: '#163936',
    wipBorder: '#2A5F58',
    success: '#45C987',
    successBg: '#19382D',
    successBorder: '#2A5A45',
    danger: '#FF7373',
    dangerBg: '#401F26',
    dangerBorder: '#6B3238',
    warning: '#FFD166',
    warningBg: '#3A301D',
    warningBorder: '#6B5424',
    info: '#59BFFF',
    infoBg: '#19334A',
    infoBorder: '#2A5578',
    neutral: '#E5EDF6',
    neutralBg: '#26394F',
    neutralBorder: '#354B62',
};

const DARK_CHROME_VARS = {
    '--surface-page': DARK_CHROME.surfacePage,
    '--surface-card': DARK_CHROME.surfaceCard,
    '--surface-muted': '#152636',
    '--surface-raised': DARK_CHROME.surfaceRaised,
    '--surface-hover': DARK_CHROME.surfaceHover,
    '--text-primary': DARK_CHROME.textPrimary,
    '--text-secondary': DARK_CHROME.textSecondary,
    '--text-tertiary': DARK_CHROME.textTertiary,
    '--border-subtle': DARK_CHROME.borderSubtle,
    '--page-bg': DARK_CHROME.surfacePage,
    '--text-navy': DARK_CHROME.textPrimary,
    '--text-muted': DARK_CHROME.textSecondary,
    '--card-border': DARK_CHROME.borderSubtle,
    '--control-border': DARK_CHROME.borderSubtle,
    '--bg-body': DARK_CHROME.surfacePage,
    '--bg-card': DARK_CHROME.surfaceCard,
    '--bg-table-header': DARK_CHROME.surfaceRaised,
    '--bg-table-row-alt': '#203247',
    '--border-color': DARK_CHROME.borderSubtle,
};

const DARK_STATUS_VARS = {
    '--status-created': DARK_STATUS.created,
    '--status-created-bg': DARK_STATUS.createdBg,
    '--status-created-border': DARK_STATUS.createdBorder,
    '--status-accepted': DARK_STATUS.accepted,
    '--status-accepted-bg': DARK_STATUS.acceptedBg,
    '--status-accepted-border': DARK_STATUS.acceptedBorder,
    '--status-started': DARK_STATUS.started,
    '--status-started-bg': DARK_STATUS.startedBg,
    '--status-started-border': DARK_STATUS.startedBorder,
    '--status-wip': DARK_STATUS.wip,
    '--status-wip-bg': DARK_STATUS.wipBg,
    '--status-wip-border': DARK_STATUS.wipBorder,
    '--status-success': DARK_STATUS.success,
    '--status-success-bg': DARK_STATUS.successBg,
    '--status-success-border': DARK_STATUS.successBorder,
    '--status-danger': DARK_STATUS.danger,
    '--status-danger-bg': DARK_STATUS.dangerBg,
    '--status-danger-border': DARK_STATUS.dangerBorder,
    '--status-warning': DARK_STATUS.warning,
    '--status-warning-bg': DARK_STATUS.warningBg,
    '--status-warning-border': DARK_STATUS.warningBorder,
    '--status-info': DARK_STATUS.info,
    '--status-info-bg': DARK_STATUS.infoBg,
    '--status-info-border': DARK_STATUS.infoBorder,
    '--status-neutral': DARK_STATUS.neutral,
    '--status-neutral-bg': DARK_STATUS.neutralBg,
    '--status-neutral-border': DARK_STATUS.neutralBorder,
};

export { DARK_CHROME_VARS, DARK_STATUS_VARS };

const CHROME = {
    surfacePage: '#F2F5F9',
    surfaceCard: '#FFFFFF',
    surfaceMuted: '#F7F9FC',
    surfaceHover: '#EEF2F7',
    textPrimary: '#24364B',
    textSecondary: '#718096',
    textTertiary: '#8A97A8',
    borderSubtle: '#DCE4ED',
};

const STATUS = {
    success: '#278653',
    successHover: '#3FA56A',
    warning: '#C47B12',
    warningHover: '#D99116',
    danger: '#D64545',
    info: '#1769AA',
    activity: '#0E7C86',
    activityHover: '#14919B',
};

/**
 * Completed vs Active is a product series, not council brand.
 * Brand red must not mean "completed" or "error" in the same chart.
 */
const CHART_SERIES = {
    /* Product series — independent of council brand */
    completed: '#087FC1',
    completedHover: '#066BA3',
    completedBorder: '#055A89',
    active: '#7B8A9B',
    activeHover: '#5B6B7C',
    activeBorder: '#4B5D70',
};

/** Neutral series for secondary chart data. Brand is applied in getChartColors(). */
const CHART_NEUTRAL = [
    '#4B5D70',
    '#718096',
    '#8A97A8',
    '#A8B6C4',
    '#C5D0DC',
    '#D3DCE6',
];

const CHART_NEUTRAL_HOVER = [
    '#3A4A5B',
    '#5B6B7C',
    '#718096',
    '#8A97A8',
    '#A8B6C4',
    '#C5D0DC',
];

/** Lighter steel steps so spend/category slices read on navy cards. */
const DARK_CHART_NEUTRAL = [
    '#4F8FB8',
    '#6EA3C6',
    '#8FB6D2',
    '#A7B6C9',
    '#C5D3E2',
];

const DARK_CHART_NEUTRAL_HOVER = [
    '#63A0C6',
    '#82B4D2',
    '#A3C4DC',
    '#B9C7D8',
    '#D6E0EA',
];

const DEFAULT = {
    primary: '#0288D1',
    primaryMid: '#0277BD',
    primaryDark: '#01579B',
    primaryLight: '#03A9F4',
    accent: '#f0c014',
    navTheme: 'dark',
    navBg: '#01579b',
    navBgMid: '#0277bd',
    navBgEnd: '#0288d1',
    navFg: '#ffffff',
    navMark: 'contain',
    mapLat: 53.65,
    mapLng: -6.68,
    mapZoom: 10,
    ...CHROME,
    ...STATUS,
};

const THEMES = {
    demo: { ...DEFAULT, navMark: 'crop-left' },
    development: { ...DEFAULT, navMark: 'crop-left' },
    civic: {
        ...DEFAULT,
        primary: '#01579b',
        primaryMid: '#0277bd',
        primaryDark: '#01467c',
        primaryLight: '#0288D1',
        accent: '#f9ad3c',
        navBg: '#01579b',
        navBgMid: '#01579b',
        navBgEnd: '#0277bd',
        navMark: 'contain',
        mapLat: 53.35,
        mapLng: -6.26,
        mapZoom: 12,
    },
    sdcc: {
        ...DEFAULT,
        primary: '#021d3f',
        primaryMid: '#0a3a6b',
        primaryDark: '#011530',
        primaryLight: '#0a3a6b',
        accent: '#f9ad3c',
        navTheme: 'light',
        navBg: '#E1E5E8',
        navBgMid: '#E1E5E8',
        navBgEnd: '#E1E5E8',
        navFg: '#021d3f',
        navMark: 'contain',
        mapLat: 53.29,
        mapLng: -6.40,
        mapZoom: 11,
    },
    carlow: {
        ...DEFAULT,
        primary: '#0e7490',
        primaryMid: '#14919b',
        primaryDark: '#155e75',
        primaryLight: '#0891b2',
        accent: '#22d3ee',
        navTheme: 'light',
        navBg: '#ffffff',
        navBgMid: '#ffffff',
        navBgEnd: '#ffffff',
        navFg: '#155e75',
        navMark: 'crop-left',
        mapLat: 52.84,
        mapLng: -6.93,
        mapZoom: 11,
    },
    westmeath: {
        ...DEFAULT,
        primary: '#b91c1c',
        primaryMid: '#dc2626',
        primaryDark: '#7f1d1d',
        primaryLight: '#ef4444',
        accent: '#f59e0b',
        navTheme: 'light',
        navBg: '#ffffff',
        navBgMid: '#ffffff',
        navBgEnd: '#ffffff',
        navFg: '#7f1d1d',
        navMark: 'crop-right',
        mapLat: 53.42,
        mapLng: -7.35,
        mapZoom: 10,
    },
    meathcc: {
        ...DEFAULT,
        accent: '#f2b705',
        navMark: 'contain',
        mapLat: 53.65,
        mapLng: -6.68,
        mapZoom: 10,
    },
    sks: {
        ...DEFAULT,
        primary: '#b91c1c',
        primaryMid: '#dc2626',
        primaryDark: '#7f1d1d',
        primaryLight: '#ef4444',
        accent: '#f59e0b',
        navTheme: 'light',
        navBg: '#ffffff',
        navBgMid: '#ffffff',
        navBgEnd: '#ffffff',
        navFg: '#7f1d1d',
        navMark: 'contain',
        mapLat: 53.29,
        mapLng: -6.40,
        mapZoom: 11,
    },
    watcc: {
        ...DEFAULT,
        navMark: 'contain',
        mapLat: 52.26,
        mapLng: -7.11,
        mapZoom: 11,
    },
};

function parseHex(hex) {
    const match = String(hex || '').trim().match(HEX6);
    return match ? match[1] : null;
}

export function hexToRgb(hex, fallback = DEFAULT.primary) {
    const source = parseHex(hex) || parseHex(fallback) || '0288D1';
    const n = parseInt(source, 16);
    if (Number.isNaN(n)) {
        const fb = parseInt((parseHex(DEFAULT.primary) || '0288D1'), 16);
        return [(fb >> 16) & 255, (fb >> 8) & 255, fb & 255].join(', ');
    }
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255].join(', ');
}

function pick(envKey, fallback) {
    const value = process.env[envKey];
    return value && String(value).trim() ? String(value).trim() : fallback;
}

export function getTheme() {
    const envName = process.env.REACT_APP_GLOB_APP_ENV || 'demo';
    const base = THEMES[envName] || DEFAULT;
    const primary = pick('REACT_APP_BRAND_PRIMARY', base.primary);
    return {
        envName,
        primary,
        primaryMid: pick('REACT_APP_BRAND_PRIMARY_MID', base.primaryMid),
        primaryDark: pick('REACT_APP_BRAND_PRIMARY_DARK', base.primaryDark),
        primaryLight: pick('REACT_APP_BRAND_PRIMARY_LIGHT', base.primaryLight),
        accent: pick('REACT_APP_BRAND_ACCENT', base.accent),
        navTheme: pick('REACT_APP_NAV_THEME', base.navTheme),
        navBg: pick('REACT_APP_NAV_BG', base.navBg),
        navBgMid: pick('REACT_APP_NAV_BG_MID', base.navBgMid),
        navBgEnd: pick('REACT_APP_NAV_BG_END', base.navBgEnd),
        navFg: pick('REACT_APP_NAV_FG', base.navFg),
        navMark: pick('REACT_APP_NAV_MARK', base.navMark),
        surfacePage: pick('REACT_APP_SURFACE_PAGE', base.surfacePage),
        surfaceCard: pick('REACT_APP_SURFACE_CARD', base.surfaceCard),
        surfaceMuted: pick('REACT_APP_SURFACE_MUTED', base.surfaceMuted),
        surfaceHover: pick('REACT_APP_SURFACE_HOVER', base.surfaceHover),
        textPrimary: pick('REACT_APP_TEXT_PRIMARY', base.textPrimary),
        textSecondary: pick('REACT_APP_TEXT_SECONDARY', base.textSecondary),
        textTertiary: pick('REACT_APP_TEXT_TERTIARY', base.textTertiary),
        borderSubtle: pick('REACT_APP_BORDER_SUBTLE', base.borderSubtle),
        focusRing: pick('REACT_APP_FOCUS_RING', primary),
        success: base.success,
        successHover: base.successHover,
        warning: base.warning,
        warningHover: base.warningHover,
        danger: base.danger,
        info: base.info,
        activity: base.activity,
        activityHover: base.activityHover,
        mapLat: Number.parseFloat(pick('REACT_APP_MAP_LAT', base.mapLat)),
        mapLng: Number.parseFloat(pick('REACT_APP_MAP_LNG', base.mapLng)),
        mapZoom: Number.parseInt(pick('REACT_APP_MAP_ZOOM', base.mapZoom), 10),
    };
}

export function getMapView() {
    const t = getTheme();
    const lat = Number.isFinite(t.mapLat) ? t.mapLat : DEFAULT.mapLat;
    const lng = Number.isFinite(t.mapLng) ? t.mapLng : DEFAULT.mapLng;
    const zoom = Number.isFinite(t.mapZoom) && t.mapZoom > 0 ? t.mapZoom : DEFAULT.mapZoom;
    return { center: { lat, lng }, zoom };
}

export function getChartColors(themeMode) {
    const t = getTheme();
    const dark = themeMode === 'dark' || (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark');
    const textPrimary = dark ? DARK_CHROME.textPrimary : t.textPrimary;
    const textSecondary = dark ? DARK_CHROME.textSecondary : t.textSecondary;
    const textTertiary = dark ? DARK_CHROME.textTertiary : t.textTertiary;
    const product = dark ? DARK_PRODUCT.primary : t.primary;
    return {
        primary: dark ? DARK_PRODUCT.primary : t.primary,
        primaryMid: dark ? DARK_PRODUCT.primaryMid : t.primaryMid,
        primaryDark: dark ? DARK_PRODUCT.primaryDark : t.primaryDark,
        primaryLight: dark ? DARK_PRODUCT.primaryLight : t.primaryLight,
        accent: t.accent,
        /* Completed = product blue, Active = blue-grey. SLA uses success/warning. */
        completed: dark ? DARK_PRODUCT.primary : CHART_SERIES.completed,
        completedHover: dark ? DARK_PRODUCT.primaryLight : CHART_SERIES.completedHover,
        completedBorder: dark ? DARK_PRODUCT.primaryMid : CHART_SERIES.completedBorder,
        active: dark ? '#A8B9CC' : CHART_SERIES.active,
        activeHover: dark ? '#C5D3E2' : CHART_SERIES.activeHover,
        activeBorder: dark ? '#8FA3B8' : CHART_SERIES.activeBorder,
        /* Spend: 2 product blues, then steel neutrals that stay visible on navy. */
        spend: dark
            ? [DARK_PRODUCT.primary, DARK_PRODUCT.primaryLight, ...DARK_CHART_NEUTRAL]
            : [t.primaryDark, t.primary, ...CHART_NEUTRAL.slice(0, 5)],
        spendHover: dark
            ? [DARK_PRODUCT.primaryLight, '#5EBEF0', ...DARK_CHART_NEUTRAL_HOVER]
            : [t.primary, t.primaryMid, ...CHART_NEUTRAL_HOVER.slice(0, 5)],
        category: dark
            ? [DARK_PRODUCT.primary, DARK_PRODUCT.primaryLight, ...DARK_CHART_NEUTRAL.slice(0, 3)]
            : [t.primaryDark, t.primary, ...CHART_NEUTRAL.slice(0, 3)],
        categoryHover: dark
            ? [DARK_PRODUCT.primaryLight, '#5EBEF0', ...DARK_CHART_NEUTRAL_HOVER.slice(0, 3)]
            : [t.primary, t.primaryMid, ...CHART_NEUTRAL_HOVER.slice(0, 3)],
        categorySoft: dark ? DARK_CHART_NEUTRAL : CHART_NEUTRAL,
        categorySoftHover: dark ? DARK_CHART_NEUTRAL_HOVER : CHART_NEUTRAL_HOVER,
        sliceBorder: dark ? DARK_CHROME.surfaceCard : '#FFFFFF',
        /* SLA / incidents: green compliance, orange attention. Same in every council. */
        success: dark ? DARK_STATUS.success : t.success,
        successHover: dark ? '#63D6A0' : t.successHover,
        warning: dark ? DARK_STATUS.warning : t.warning,
        warningHover: dark ? '#FFE08A' : t.warningHover,
        danger: dark ? DARK_STATUS.danger : t.danger,
        dangerHover: dark ? '#FF8F8F' : '#C0392B',
        navy: textPrimary,
        textPrimary,
        textSecondary: dark ? '#C5D3E2' : textSecondary,
        textTertiary,
        grid: dark ? 'rgba(230, 237, 245, 0.12)' : `rgba(${hexToRgb(t.textPrimary, t.primary)}, 0.06)`,
        gridZero: dark ? 'rgba(230, 237, 245, 0.22)' : `rgba(${hexToRgb(t.textPrimary, t.primary)}, 0.1)`,
        tooltipBg: dark ? DARK_CHROME.surfaceRaised : textPrimary,
        tooltipColor: dark ? DARK_CHROME.textPrimary : '#FFFFFF',
        product,
    };
}

export { CHROME, STATUS, CHART_SERIES, CHART_NEUTRAL, DEFAULT, THEMES };
